import { useState } from 'react';
import { daysBetween } from '../../lib/dates';

export interface DayRange {
  checkIn: string | null; // ISO YYYY-MM-DD
  checkOut: string | null;
}
export interface DateRangePickerProps {
  checkIn: string | null;
  checkOut: string | null;
  today: string; // ISO
  onChange: (r: DayRange) => void;
  onDone?: () => void;
  // 'night' (default) reads the span as exclusive nights (bookings); 'day' reads
  // it as inclusive days (staff leave — both endpoints count).
  unit?: 'night' | 'day';
  // Armed-field mode (staff-food-at-house): which field a pick edits, shown as
  // two field chips above the grid. Provided (with both dates set) → the
  // armed reducer drives picks; absent → the classic range reducer.
  armed?: ArmedField;
  onArmedChange?: (a: ArmedField) => void;
  fieldLabels?: { from: string; to: string };
  // ONE DAY, NOT A RANGE (a viewing's day). A pick sets `checkIn` alone; the
  // grid marks it as the start and nothing as in range; the footer names the
  // day. Everything else — the month grid, the chrome, Done — is the range
  // picker's, so the two read as one control (Sir, 2026-09-02).
  single?: boolean;
}

const DOWS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function nights(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  return daysBetween(a, b);
}
const isoOf = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Pure range-pick reducer (unit-tested). Clicking a day starts a fresh range
// (no check-in, range complete, or click before check-in) or completes it.
export function pickDate(r: DayRange, date: string): DayRange {
  if (!r.checkIn || r.checkOut || date < r.checkIn) return { checkIn: date, checkOut: null };
  return { checkIn: r.checkIn, checkOut: date };
}

const addIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + n));
  return dt.toISOString().slice(0, 10);
};

export type ArmedField = 'in' | 'out';

// Armed-field reducer (unit-tested) — staff-food-at-house's picker upgrade.
// One field is armed; a pick edits ONLY that field. Moving check-in past
// check-out SLIDES the stay (nights preserved — moving a booking keeps its
// length); a day before check-in while armed on check-out reads as a new
// check-in. Picking check-in advances the arm so a full range is two taps;
// single-field edits are equally first-class (pick, then Done).
export function pickArmedDate(
  r: { checkIn: string; checkOut: string },
  date: string,
  armed: ArmedField,
): { range: { checkIn: string; checkOut: string }; armed: ArmedField } {
  // Span preserved verbatim — a zero-night (same-day) stay slides as zero
  // nights, never silently becoming an overnight booking (Codex P2).
  const span = Math.max(0, daysBetween(r.checkIn, r.checkOut));
  if (armed === 'in') {
    const checkOut = r.checkOut < date ? addIso(date, span) : r.checkOut;
    return { range: { checkIn: date, checkOut }, armed: 'out' };
  }
  if (date < r.checkIn) return { range: { checkIn: date, checkOut: addIso(date, span) }, armed: 'out' };
  return { range: { checkIn: r.checkIn, checkOut: date }, armed: 'out' };
}

// Footer copy for the picker (unit-tested). Nights are exclusive (bookings);
// days are inclusive (leave spans both endpoints, so a 1-day count for same-day).
export function rangeFootLabel(checkIn: string | null, checkOut: string | null, unit: 'night' | 'day' = 'night'): string {
  const n = nights(checkIn, checkOut);
  if (n != null) {
    const count = unit === 'day' ? n + 1 : n;
    const noun = unit === 'day' ? 'day' : 'night';
    return `${count} ${count === 1 ? noun : `${noun}s`}`;
  }
  const pickingOut = !!checkIn && !checkOut;
  if (unit === 'day') return pickingOut ? 'Now pick the last day' : 'Pick the first day';
  return pickingOut ? 'Now pick check-out' : 'Pick check-in';
}

// Inline range calendar — renders the prototype's calhead/calgrid/cal-day markup
// verbatim (start/end/inrange). Lives inside the booking form's .calinline slide.
export function DateRangePicker({ checkIn, checkOut, today, onChange, onDone, unit = 'night', armed, onArmedChange, fieldLabels, single = false }: DateRangePickerProps) {
  const seed = checkIn || today;
  const [vy, setVy] = useState(Number(seed.slice(0, 4)));
  const [vm, setVm] = useState(Number(seed.slice(5, 7)) - 1); // 0-based
  const navM = (dir: number) => {
    let m = vm + dir, y = vy;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setVm(m); setVy(y);
  };
  const pick = (s: string) => {
    if (single) {
      onChange({ checkIn: s, checkOut: null });
      return;
    }
    if (armed && checkIn && checkOut) {
      const next = pickArmedDate({ checkIn, checkOut }, s, armed);
      onChange(next.range);
      onArmedChange?.(next.armed);
      return;
    }
    onChange(pickDate({ checkIn, checkOut }, s));
  };

  // "Thu 16 Jul" face for the field chips — composed from parts (no locale comma).
  const chipFace = (iso: string | null) => {
    if (!iso) return 'Select…';
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
    return `${dt.toLocaleDateString('en-GB', { weekday: 'short' })} ${dt.getDate()} ${dt.toLocaleDateString('en-GB', { month: 'short' })}`;
  };
  const labs = fieldLabels ?? (unit === 'day' ? { from: 'From', to: 'To' } : { from: 'Check-in', to: 'Check-out' });

  // Mon-start grid with leading blanks.
  const first = new Date(vy, vm, 1);
  const off = (first.getDay() + 6) % 7;
  const dim = new Date(vy, vm + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < off; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const title = new Date(vy, vm, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <>
      {armed && (
        <div className="fieldchips">
          <button type="button" className={'fieldchip' + (armed === 'in' ? ' armed' : '')} onClick={() => onArmedChange?.('in')}>
            <span className="fl">{labs.from}</span><b>{chipFace(checkIn)}</b>
          </button>
          <button type="button" className={'fieldchip' + (armed === 'out' ? ' armed' : '')} onClick={() => onArmedChange?.('out')}>
            <span className="fl">{labs.to}</span><b>{chipFace(checkOut)}</b>
          </button>
        </div>
      )}
      <div className="calhead">
        <button type="button" onClick={() => navM(-1)} aria-label="Previous month">‹</button>
        <div className="caltitle">{title}</div>
        <button type="button" onClick={() => navM(1)} aria-label="Next month">›</button>
      </div>
      <div className="calgrid">
        {DOWS.map((d) => <div key={d} className="cdow">{d}</div>)}
        {cells.map((d, i) => {
          if (d == null) return <div key={i} />;
          const s = isoOf(vy, vm, d);
          let cls = 'cal-day';
          if (s === checkIn) cls += single ? ' start single' : ' start';
          else if (!single && s === checkOut) cls += ' end';
          else if (!single && checkIn && checkOut && s > checkIn && s < checkOut) cls += ' inrange';
          return <button key={i} type="button" className={cls} onClick={() => pick(s)}>{d}</button>;
        })}
      </div>
      <div className="calfoot">
        <span>{single ? (checkIn ? chipFace(checkIn) : 'Pick the day') : rangeFootLabel(checkIn, checkOut, unit)}</span>
        {onDone && <button type="button" className="btn primary" style={{ padding: '6px 14px' }} onClick={onDone}>Done</button>}
      </div>
    </>
  );
}
